import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import Countdown from "../components/Countdown";
import { Skeleton } from "../components/ui/skeleton";
import { Truck, RotateCcw, ShieldCheck, Wallet } from "lucide-react";

const USP_ICONS = [Wallet, Truck, RotateCcw, ShieldCheck];

function Hero({ c }) {
  return (
    <section className="relative w-full h-[82vh] sm:h-[88vh] bg-zinc-950 overflow-hidden flex items-end"
      data-testid="home-hero">
      <img src={c.image} alt={c.headline} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: `linear-gradient(to top, rgba(7,7,8,${c.overlay ?? 0.55}), rgba(7,7,8,0.1) 60%, transparent)` }} />
      <div className={`relative p-6 sm:p-12 lg:p-16 max-w-3xl ${c.align === "center" ? "mx-auto text-center" : ""}`}>
        <Reveal>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.85] tracking-tight text-white uppercase whitespace-pre-line">
            {c.headline}
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-zinc-300 mt-5 max-w-md text-sm sm:text-base">{c.subtext}</p>
        </Reveal>
        {c.countdown && (
          <Reveal delay={0.15}>
            <div className="mt-6"><Countdown target={c.countdown} /></div>
          </Reveal>
        )}
        {c.button_label && (
          <Reveal delay={0.2}>
            <Link to={c.button_link || "/shop"}
              className="inline-block mt-7 btn-accent px-8 py-4 text-sm font-bold uppercase tracking-widest"
              data-testid="hero-cta">{c.button_label}</Link>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function FeaturedProducts({ c, pmap }) {
  const items = (c.product_ids || []).map((id) => pmap[id]).filter(Boolean);
  if (!items.length) return null;
  return (
    <section className="py-14 px-4 sm:px-8 max-w-7xl mx-auto">
      <Reveal><h2 className="font-display text-4xl sm:text-5xl text-white uppercase tracking-wide mb-8">{c.heading || "Featured"}</h2></Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
        {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
    </section>
  );
}

function FeaturedCollection({ c }) {
  return (
    <section className="py-14 px-4 sm:px-8 max-w-7xl mx-auto">
      <Reveal>
        <Link to={c.button_link || "/collections"} className="group block relative h-[60vh] overflow-hidden bg-zinc-950">
          <img src={c.image} alt={c.heading} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
          <div className="relative h-full flex flex-col justify-center p-8 sm:p-16 max-w-xl">
            <p className="text-accent text-xs uppercase tracking-widest font-semibold mb-3">Collection</p>
            <h2 className="font-display text-5xl sm:text-7xl text-white uppercase tracking-tight leading-none">{c.heading}</h2>
            <p className="text-zinc-300 mt-4 text-sm max-w-sm">{c.text}</p>
            <span className="inline-block mt-6 btn-accent px-7 py-3.5 text-sm font-bold uppercase tracking-widest w-fit">{c.button_label || "Explore"}</span>
          </div>
        </Link>
      </Reveal>
    </section>
  );
}

function PromoBanner({ c }) {
  const tiles = c.tiles || [];
  return (
    <section className="py-6 px-4 sm:px-8 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
      {tiles.map((t, i) => (
        <Reveal key={i} delay={i * 0.08}>
          <Link to={t.link || "/shop"} className="group relative block h-80 sm:h-[480px] overflow-hidden bg-zinc-950">
            <img src={t.image} alt={t.heading} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <h3 className="font-display text-5xl text-white uppercase tracking-tight">{t.heading}</h3>
              <span className="text-xs uppercase tracking-widest text-white mt-2 border-b border-accent-ss w-fit pb-0.5">{t.button_label || "Shop"}</span>
            </div>
          </Link>
        </Reveal>
      ))}
    </section>
  );
}

function USPStrip({ c }) {
  return (
    <section className="py-12 px-4 sm:px-8 border-y border-zinc-900 bg-[#09090b]">
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
        {(c.items || []).map((it, i) => {
          const Icon = USP_ICONS[i % USP_ICONS.length];
          return (
            <div key={i} className="flex flex-col items-center text-center gap-2">
              <Icon className="text-accent" size={26} />
              <p className="text-sm font-semibold text-zinc-100 uppercase tracking-wider">{it.title}</p>
              <p className="text-xs text-zinc-500">{it.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Editorial({ c }) {
  return (
    <section className="py-14 px-4 sm:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
      <Reveal><div className="aspect-[4/5] overflow-hidden bg-zinc-950"><img src={c.image} alt={c.heading} className="w-full h-full object-cover" /></div></Reveal>
      <Reveal delay={0.1}>
        <div>
          <h2 className="font-display text-4xl sm:text-6xl text-white uppercase tracking-tight leading-none">{c.heading}</h2>
          <p className="text-zinc-400 mt-5 leading-relaxed">{c.text}</p>
          {c.button_label && <Link to={c.button_link || "/shop"} className="inline-block mt-6 btn-accent px-7 py-3.5 text-sm font-bold uppercase tracking-widest">{c.button_label}</Link>}
        </div>
      </Reveal>
    </section>
  );
}

function Newsletter({ c }) {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await http.post("/newsletter", { email, source: "homepage" }); toast.success("Welcome to the crew."); setEmail(""); }
    catch { toast.error("Could not subscribe"); }
  };
  return (
    <section className="py-20 px-4 text-center" data-testid="home-newsletter">
      <Reveal>
        <h2 className="font-display text-5xl sm:text-7xl text-white uppercase tracking-tight">{c.heading || "Join The Crew"}</h2>
        <p className="text-zinc-400 mt-4 max-w-md mx-auto">{c.text}</p>
        <form onSubmit={submit} className="flex max-w-md mx-auto mt-8">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            data-testid="home-newsletter-input"
            className="flex-1 bg-zinc-900 border border-zinc-800 px-4 py-3.5 text-sm focus:border-accent-ss" />
          <button className="btn-accent px-7 text-sm font-bold uppercase tracking-widest" data-testid="home-newsletter-submit">Subscribe</button>
        </form>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const [blocks, setBlocks] = useState(null);
  const [pmap, setPmap] = useState({});
  const { track } = useStore();

  useEffect(() => {
    track("page_view", {});
    Promise.all([http.get("/homepage"), http.get("/products?limit=100")]).then(([b, p]) => {
      setBlocks(b.data);
      const map = {};
      (p.data.items || []).forEach((x) => { map[x.id] = x; });
      setPmap(map);
    }).catch(() => setBlocks([]));
  }, [track]);

  if (!blocks) return <div className="p-8 space-y-4"><Skeleton className="h-[80vh] w-full bg-zinc-900" /></div>;

  return (
    <div>
      {blocks.map((b) => {
        const c = b.config || {};
        switch (b.type) {
          case "hero": return <Hero key={b.id} c={c} />;
          case "marquee": return <Marquee key={b.id} placement="homepage" />;
          case "featured_products": return <FeaturedProducts key={b.id} c={c} pmap={pmap} />;
          case "featured_collection": return <FeaturedCollection key={b.id} c={c} />;
          case "promo_banner": return <PromoBanner key={b.id} c={c} />;
          case "usp_strip": return <USPStrip key={b.id} c={c} />;
          case "editorial": return <Editorial key={b.id} c={c} />;
          case "newsletter": return <Newsletter key={b.id} c={c} />;
          default: return null;
        }
      })}
    </div>
  );
}
